import React from 'react';
import { useDrop } from 'react-dnd';
import { MagnifyingGlassIcon, DotsSixVerticalIcon, TrayIcon } from '@phosphor-icons/react';
import classNames from '../../../../utils/classnames';

import { Input } from '../../../../components/input/Input';
import { ToggleGroup } from '../../../../components/toggle-group/ToggleGroup';
import { SpinnerBlock } from '../../../../components/Spinner';
import { EmptyState } from '../../../../components/empty-state/EmptyState';
import type { PlanningBoardBacklogOrder, PlanningBoardTruckOrder, Region } from '../../models';
import type { DragItem } from '../PlanningBoardPage';
import { OrderCard } from './OrderCard';

// ============================================================================
// Types
// ============================================================================

interface RegionCounts {
  all: number;
  benelux: number;
  france: number;
  germany: number;
  forwarding: number;
}

interface OrderBacklogProps {
  orders: PlanningBoardBacklogOrder[];
  counts: RegionCounts;
  plannedCount: number;
  totalCount: number;
  isLoading: boolean;
  dateType: 'load' | 'unload';
  onDateTypeChange: (type: 'load' | 'unload') => void;
  region: Region;
  onRegionChange: (region: Region) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onOrderDrop: (
    order: PlanningBoardBacklogOrder | PlanningBoardTruckOrder,
    targetTruckId: string | null,
    sourceTruckId: string | null,
  ) => void;
}

// ============================================================================
// Region chips configuration
// ============================================================================

const REGIONS: { key: Region; label: string; dotColor: string | null }[] = [
  { key: 'all', label: 'Alle', dotColor: null },
  { key: 'benelux', label: 'Benelux', dotColor: 'bg-bu-benelux' },
  { key: 'france', label: 'France', dotColor: 'bg-bu-france' },
  { key: 'germany', label: 'Germany', dotColor: 'bg-bu-germany' },
  { key: 'forwarding', label: 'Forwarding', dotColor: 'bg-bu-forwarding' },
];

// ============================================================================
// Component
// ============================================================================

/** Left rail of the planning board: filters + the unassigned orders. */
export const OrderBacklog: React.FC<OrderBacklogProps> = ({
  orders,
  counts,
  plannedCount,
  totalCount,
  isLoading,
  dateType,
  onDateTypeChange,
  region,
  onRegionChange,
  searchQuery,
  onSearchChange,
  onOrderDrop,
}) => {
  // Drop zone for removing orders from trucks
  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>(
    () => ({
      accept: 'order',
      canDrop: (item) => {
        // Can only drop if order is from a truck
        return item.sourceTruckId !== null;
      },
      drop: (item) => {
        // Remove order from truck (assign to null)
        onOrderDrop(item.order, null, item.sourceTruckId);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onOrderDrop],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Filter panel */}
      <div className="card flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="heading-two">Te plannen</h2>
          <span className="text-xs font-medium text-fg-muted">
            {plannedCount} / {totalCount} gepland
          </span>
        </div>

        {/* Region chips */}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Business unit">
          {REGIONS.map((r) => {
            const isActive = region === r.key;
            return (
              <button
                key={r.key}
                type="button"
                aria-pressed={isActive}
                className={classNames(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                  isActive
                    ? 'border-brand-border bg-brand-subtle text-brand-text'
                    : 'border-border bg-surface-sunken text-fg-secondary hover:border-border-strong hover:text-fg',
                )}
                onClick={() => onRegionChange(r.key)}
              >
                {r.dotColor && <span className={classNames('h-[7px] w-[7px] shrink-0 rounded-full', r.dotColor)} />}
                <span>{r.label}</span>
                <span className={classNames('font-medium', isActive ? 'text-brand-text' : 'text-fg-muted')}>
                  {counts[r.key]}
                </span>
              </button>
            );
          })}
        </div>

        <Input
          type="text"
          value={searchQuery}
          onChange={onSearchChange}
          placeholder="Zoek order, klant of plaats"
          iconLeft={<MagnifyingGlassIcon className="input-icon" />}
        />

        <ToggleGroup
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
          options={[
            { value: 'load', label: 'Laaddatum' },
            { value: 'unload', label: 'Losdatum' },
          ]}
          value={dateType}
          onChange={onDateTypeChange}
        />
      </div>

      <div className="flex items-center gap-1.5 px-0.5 text-2xs font-medium text-fg-muted">
        <DotsSixVerticalIcon className="h-3.5 w-3.5" weight="bold" />
        Sleep een order naar een voertuig
      </div>

      {/* Pending orders — one sunken panel that is also the drop target for unassigning */}
      <div
        ref={(node) => {
          drop(node);
        }}
        className={classNames(
          'flex min-h-0 flex-1 flex-col gap-2 overflow-auto rounded-lg border p-2 transition-colors',
          isOver && canDrop ? 'border-dashed border-brand bg-brand-subtle' : 'border-border bg-surface-sunken',
        )}
      >
        {isLoading ? (
          <SpinnerBlock message="Orders laden…" className="min-h-40" />
        ) : orders.length === 0 ? (
          <EmptyState
            className="flex-1"
            icon={<TrayIcon />}
            title={isOver && canDrop ? 'Laat los om de order van het voertuig te halen' : 'Geen orders om te plannen'}
            description={
              isOver && canDrop ? undefined : 'Alle orders voor deze dag en filters zijn toegewezen aan een voertuig.'
            }
          />
        ) : (
          <>
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} sourceTruckId={null} compact={true} />
            ))}
            {isOver && canDrop && (
              <div className="flex flex-1 items-center justify-center py-3 text-center text-xs font-medium text-brand-text">
                Laat los om de order van het voertuig te halen
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
